// The list's search, filters, sort, grouping and team strip (spec C7.2, C7.3; Renewals board). Pure
// functions over the rows already on the page, so the rules are tested without a browser. The state
// lives in the address, so Back from the Record returns to the list exactly as it was left (C7.4).
import type { CurrentStep, IncreaseRule, RenewalRow } from '../auth/api';
import type { Role } from '../roles';
import { managesDepartment } from '../roles';
import {
  isIncrease,
  listDate,
  needsAttention,
  needsIncreaseReview,
  nyToday,
  shortLine,
  stageOf,
  statuses,
  type Group,
  type TabKey,
} from './renewal-lines';
import { rowReason, shownHandoff } from './ae-handoff-lines';
import { isOutcome, outcomeLabel } from './closed-lines';

export type ExpiresWithin = 'any' | '14' | '30' | 'over30';
/**
 * The Premium change filter (design Oct 30): "over" is at or over the increase rule, any answer
 * state; "review" narrows that to Increases to look over (no answer yet, same test as the AE's
 * stat card).
 */
export type PremiumChange = 'any' | 'over' | 'review';
export type SortKey =
  | 'insured'
  | 'handledBy'
  | 'policy'
  | 'line'
  | 'days'
  | 'prior'
  | 'renewal'
  | 'change'
  | 'status'
  | 'carrier'
  | 'reason'
  | 'actBy'
  | 'from'
  | 'sent'
  | 'result';
export type SortDir = 'asc' | 'desc';
export type GroupMode = 'carrier' | 'person' | 'none';

export type ListState = {
  /** The sidebar search: insured or policy number, across every tab. */
  q: string;
  carriers: string[];
  lines: string[];
  expires: ExpiresWithin;
  premiumChange: PremiumChange;
  /** Handled by: person ids, and "none" for Unassigned. */
  people: string[];
  unassignedOnly: boolean;
  missed: boolean;
  /** Manager and Admin: the open handoff is past its Act by day (S15 batch 8). */
  overdue: boolean;
  /** The team strip's person (Manager). */
  person: string | null;
  /** The Closed tab's Outcome filter (S16): it narrows closed rows only. */
  outcomes: string[];
  sort: SortKey;
  dir: SortDir;
  group: GroupMode;
};

/** Unassigned in the Handled by filter. */
export const UNASSIGNED = 'none';
export const NO_CARRIER = 'Carrier not in AMS360';

const sortKeys: SortKey[] = [
  'insured',
  'handledBy',
  'policy',
  'line',
  'days',
  'prior',
  'renewal',
  'change',
  'status',
  'carrier',
  'reason',
  'actBy',
  'from',
  'sent',
  'result',
];

/** The first click on a column sorts this way; money and change start with the highest. */
export const firstDir = (key: SortKey): SortDir =>
  key === 'prior' || key === 'renewal' || key === 'change' || key === 'result' ? 'desc' : 'asc';

/**
 * The sort a tab opens with: the AE's Needs your answer and With me by Act by (spec C7.3), Finished
 * newest first; every other tab soonest to expire.
 */
export const sortDefaultOf = (tab: TabKey | null): SortKey =>
  tab === 'ask' || tab === 'withme' ? 'actBy' : tab === 'finished' ? 'result' : defaultState.sort;

export const defaultState: ListState = {
  q: '',
  carriers: [],
  lines: [],
  expires: 'any',
  premiumChange: 'any',
  people: [],
  unassignedOnly: false,
  missed: false,
  overdue: false,
  person: null,
  outcomes: [],
  sort: 'days',
  dir: 'asc',
  group: 'carrier',
};

/** Grouping by person is the Manager's and the Admin's (spec C7.3). */
export const groupModes = (role: Role): GroupMode[] =>
  managesDepartment(role) ? ['none', 'carrier', 'person'] : ['none', 'carrier'];

/** Handled by shows for the Manager, the Admin and the AE (spec C7.3), and so does its filter. */
export const filtersByPerson = (role: Role) => role !== 'Renewal VA';

export function readState(
  params: URLSearchParams,
  role: Role,
  sortDefault: SortKey = defaultState.sort,
): ListState {
  const exp = params.get('exp');
  const sort = params.get('sort') as SortKey | null;
  const key = sort && sortKeys.includes(sort) ? sort : sortDefault;
  const dir = params.get('dir');
  const group = params.get('group') as GroupMode | null;
  const manager = managesDepartment(role);
  return {
    q: params.get('q') ?? '',
    carriers: params.getAll('carrier'),
    lines: params.getAll('line'),
    expires: exp === '14' || exp === '30' || exp === 'over30' ? exp : 'any',
    premiumChange:
      params.get('chg') === 'over' ? 'over' : params.get('chg') === 'review' ? 'review' : 'any',
    people: filtersByPerson(role) ? params.getAll('by') : [],
    unassignedOnly: manager && params.get('unassigned') === '1',
    missed: manager && params.get('missed') === '1',
    overdue: manager && params.get('overdue') === '1',
    person: manager ? params.get('person') : null,
    outcomes: params.getAll('outcome').filter(isOutcome),
    sort: key,
    dir: dir === 'asc' || dir === 'desc' ? dir : firstDir(key),
    group: group && groupModes(role).includes(group) ? group : 'carrier',
  };
}

/** The list state as address parameters; defaults are left out so a plain list has a plain address. */
export function stateParams(
  s: ListState,
  sortDefault: SortKey = defaultState.sort,
): Record<string, string | string[] | null> {
  return {
    q: s.q.trim() ? s.q : null,
    carrier: s.carriers,
    line: s.lines,
    exp: s.expires === 'any' ? null : s.expires,
    chg: s.premiumChange === 'any' ? null : s.premiumChange,
    by: s.people,
    unassigned: s.unassignedOnly ? '1' : null,
    missed: s.missed ? '1' : null,
    overdue: s.overdue ? '1' : null,
    person: s.person,
    outcome: s.outcomes,
    sort: s.sort === sortDefault ? null : s.sort,
    dir: s.dir === firstDir(s.sort) ? null : s.dir,
    group: s.group === 'carrier' ? null : s.group,
  };
}

const compact = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Search: the insured's short or full name, or the policy number with or without its dashes. */
export function matchesSearch(r: RenewalRow, q: string): boolean {
  const words = q.trim().toLowerCase();
  if (!words) return true;
  const name = `${r.insured} ${r.insuredFull}`.toLowerCase();
  if (words.split(/\s+/).every((w) => name.includes(w.replace(/,$/, '')))) return true;
  const policy = compact(q);
  return policy.length > 0 && compact(r.policyNumber).includes(policy);
}

export const carrierOf = (r: RenewalRow) => r.carrier ?? NO_CARRIER;

/** Missed: expired in the last 30 days and not closed (spec C7.3; the list holds the last 30 days). */
export const isMissed = (r: RenewalRow) => r.daysLeft < 0 && stageOf(r) !== 'closed';

/** Over the stored increase rule (spec C14 Defaults), the Change cell's amber rule. */
export const overThreshold = (r: RenewalRow, rule: IncreaseRule) =>
  isIncrease(rule, r.priorPremium, r.renewalPremium);

/** The Premium change filter's helper note (design Oct 30): "15% or more, any amount." */
export const premiumChangeNote = (rule: IncreaseRule) =>
  rule.minDollars > 0
    ? `${rule.percent}% or more and at least ${moneyWhole(rule.minDollars)}.`
    : `${rule.percent}% or more, any amount.`;

const dollarsWhole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const moneyWhole = (n: number) => dollarsWhole.format(n);

function expiresIn(d: number, within: ExpiresWithin): boolean {
  if (within === '14') return d <= 14;
  if (within === '30') return d <= 30;
  if (within === 'over30') return d > 30;
  return true;
}

/** The Filters popover, the team strip and the search, applied to the rows of the tab or stat card. */
/**
 * The open handoff is past its Act by day (spec C6, the Manager's Overdue filter): waiting for the
 * AE's answer (Overdue) or with the AE and still not finished. A take over has no Act by.
 */
export const pastActBy = (r: Pick<RenewalRow, 'handoff'>, now: Date) =>
  !!r.handoff?.actBy && r.handoff.actBy < nyToday(now);

export function applyFilters(
  rows: RenewalRow[],
  s: ListState,
  rule: IncreaseRule,
  now: Date = new Date(),
): RenewalRow[] {
  return rows.filter(
    (r) =>
      matchesSearch(r, s.q) &&
      (s.carriers.length === 0 || s.carriers.includes(carrierOf(r))) &&
      (s.lines.length === 0 || s.lines.includes(r.line)) &&
      expiresIn(r.daysLeft, s.expires) &&
      (s.premiumChange === 'any' ||
        (s.premiumChange === 'over' ? overThreshold(r, rule) : needsIncreaseReview(r, rule))) &&
      (s.people.length === 0 || s.people.includes(r.handledBy?.id ?? UNASSIGNED)) &&
      (!s.unassignedOnly || r.handledBy === null) &&
      (!s.missed || isMissed(r)) &&
      (!s.overdue || pastActBy(r, now)) &&
      (s.person === null || r.handledBy?.id === s.person) &&
      // Outcome narrows the closed rows; a row not closed is not an outcome's to hide.
      (s.outcomes.length === 0 || !r.closed || s.outcomes.includes(r.closed.outcome)),
  );
}

/** How many filters are applied, for the badge on Filters (the search and the strip are not). */
export const filterCount = (s: ListState) =>
  s.carriers.length +
  s.lines.length +
  (s.expires === 'any' ? 0 : 1) +
  (s.premiumChange === 'any' ? 0 : 1) +
  s.people.length +
  (s.unassignedOnly ? 1 : 0) +
  (s.missed ? 1 : 0) +
  (s.overdue ? 1 : 0) +
  s.outcomes.length;

export const hasNarrowing = (s: ListState) => filterCount(s) > 0 || s.person !== null;

const expiresLabels: Record<ExpiresWithin, string> = {
  any: 'Any',
  '14': '14 days or less',
  '30': '30 days or less',
  over30: 'Over 30 days',
};
export const expiresOptions = (['any', '14', '30', 'over30'] as const).map((v) => ({
  value: v,
  label: expiresLabels[v],
}));

/** A line as the Filters show it (Renewals board): AMS360's name, the long auto and umbrella names cut. */
const lineLabels: Record<string, string> = {
  'Private Passenger Auto': 'Auto',
  'Umbrella (P)': 'Umbrella',
};
export const lineLabel = (line: string) => lineLabels[line] ?? line;

/** The applied filters as removable chips under the toolbar, each with what it removes. */
export function appliedChips(
  s: ListState,
  nameOf: (id: string) => string,
): { key: string; label: string; without: Partial<ListState> }[] {
  return [
    ...s.carriers.map((c) => ({
      key: `carrier:${c}`,
      label: c,
      without: { carriers: s.carriers.filter((x) => x !== c) },
    })),
    ...s.lines.map((l) => ({
      key: `line:${l}`,
      label: lineLabel(l),
      without: { lines: s.lines.filter((x) => x !== l) },
    })),
    ...(s.expires === 'any'
      ? []
      : [
          {
            key: 'exp',
            label: `Expires ${expiresLabels[s.expires].toLowerCase()}`,
            without: { expires: 'any' as const },
          },
        ]),
    ...(s.premiumChange === 'any'
      ? []
      : [
          {
            key: 'chg',
            label:
              s.premiumChange === 'review'
                ? 'Increases to look over'
                : 'Premium change: at or over the increase rule',
            without: { premiumChange: 'any' as const },
          },
        ]),
    ...s.people.map((p) => ({
      key: `by:${p}`,
      label: p === UNASSIGNED ? 'Unassigned' : nameOf(p),
      without: { people: s.people.filter((x) => x !== p) },
    })),
    ...(s.unassignedOnly
      ? [{ key: 'unassigned', label: 'Unassigned only', without: { unassignedOnly: false } }]
      : []),
    ...(s.missed ? [{ key: 'missed', label: 'Missed', without: { missed: false } }] : []),
    ...(s.overdue
      ? [{ key: 'overdue', label: 'Overdue, past Act by', without: { overdue: false } }]
      : []),
    ...s.outcomes.map((o) => ({
      key: `outcome:${o}`,
      label: `Outcome: ${outcomeLabel(o)}`,
      without: { outcomes: s.outcomes.filter((x) => x !== o) },
    })),
  ];
}

/** Everything the Filters popover and the strip set, cleared; the search, sort and grouping stay. */
export const cleared = (s: ListState): ListState => ({
  ...defaultState,
  q: s.q,
  sort: s.sort,
  dir: s.dir,
  group: s.group,
});

/** The Sort by list in Filters (Renewals board). */
export const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'days', label: 'Expires (soonest first)' },
  { key: 'insured', label: 'Insured A to Z' },
  { key: 'change', label: 'Premium change (highest first)' },
  { key: 'carrier', label: 'Carrier' },
];

const pct = (r: RenewalRow) =>
  r.priorPremium === null || r.renewalPremium === null || r.priorPremium === 0
    ? null
    : (r.renewalPremium - r.priorPremium) / r.priorPremium;

const statusOrder = Object.keys(statuses);

function sortValue(r: RenewalRow, key: SortKey, finished: boolean): string | number | null {
  switch (key) {
    case 'insured':
      return r.insured.toLowerCase();
    case 'handledBy':
      return r.handledBy?.name.toLowerCase() ?? null;
    case 'policy':
      return r.policyNumber.toLowerCase();
    case 'line':
      return shortLine(r.line).toLowerCase();
    case 'days':
      return r.daysLeft;
    case 'prior':
      return r.priorPremium;
    case 'renewal':
      return r.renewalPremium;
    case 'change':
      return pct(r);
    case 'status':
      return statusOrder.indexOf(r.status);
    case 'carrier':
      return r.carrier?.toLowerCase() ?? null;
    case 'reason':
      return rowReason(r, finished)?.label.toLowerCase() ?? null;
    case 'actBy':
      // A take over has no Act by, so those rows fall back to soonest first.
      return r.handoff?.actBy ?? null;
    case 'from':
      return shownHandoff(r, finished)?.createdBy.name.toLowerCase() ?? null;
    case 'sent':
      return shownHandoff(r, finished)?.createdAt ?? null;
    case 'result':
      return r.finishedHandoff?.finishedAt ?? null;
  }
}

/** Rows in the chosen order; blanks (no renewal premium yet) always last; ties soonest first. */
/** `finished`: the rows are the AE's Finished ones, sorted by the handoff that ended. */
export function comparator(key: SortKey, dir: SortDir, finished = false) {
  const sign = dir === 'asc' ? 1 : -1;
  return (a: RenewalRow, b: RenewalRow) => {
    const x = sortValue(a, key, finished);
    const y = sortValue(b, key, finished);
    if (x !== y) {
      if (x === null) return 1;
      if (y === null) return -1;
      const c =
        typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
      if (c !== 0) return c * sign;
    }
    return a.daysLeft - b.daysLeft || a.insured.localeCompare(b.insured);
  };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function groupMeta(rows: RenewalRow[]): string {
  const days = rows.map((r) => r.daysLeft);
  const lo = Math.min(...days);
  const hi = Math.max(...days);
  if (hi < 0) return lo === hi ? `Expired ${-lo}d ago` : `Expired ${-hi}d to ${-lo}d ago`;
  if (lo < 0) return `Expired ${-lo}d ago to ${hi} days out`;
  return lo === hi ? `Expires in ${plural(lo, 'day', 'days')}` : `Expires in ${lo} to ${hi} days`;
}

/**
 * The table's groups: Needs attention pinned first (spec C5), then one group per carrier or person,
 * the groups by their soonest renewal and the rows in the chosen order. With no grouping, one group
 * without a header holds every row in that order. On the AE's With me (`followUpsOn`, today's New
 * York day), the rows to follow up today come to the top, in a "Follow up today" group when grouped
 * (spec C6; the Renewals board).
 */
export function groupList(
  rows: RenewalRow[],
  mode: GroupMode,
  cmp: ReturnType<typeof comparator>,
  followUpsOn: string | null = null,
): Group[] {
  const dueToday = (r: RenewalRow) => !!followUpsOn && r.handoff?.followUpOn === followUpsOn;
  const ordered = [...rows].sort(cmp);
  const sorted = [...ordered.filter(dueToday), ...ordered.filter((r) => !dueToday(r))];
  if (mode === 'none') {
    return sorted.length
      ? [{ key: 'all', label: '', attention: false, rows: sorted, meta: '' }]
      : [];
  }
  const attention = sorted.filter(needsAttention);
  const followUps = sorted.filter((r) => !needsAttention(r) && dueToday(r));
  const rest = sorted.filter((r) => !needsAttention(r) && !dueToday(r));
  const groups: Group[] = [];
  if (attention.length > 0) {
    const soonest = attention.reduce((a, b) => (b.daysLeft < a.daysLeft ? b : a));
    groups.push({
      key: 'attention',
      label: 'Needs attention',
      attention: true,
      rows: attention,
      meta: `Soonest ${listDate(soonest.expirationDate)}`,
    });
  }
  if (followUps.length > 0) {
    groups.push({
      key: 'followup',
      label: 'Follow up today',
      attention: false,
      rows: followUps,
      meta: '',
    });
  }
  // Keyed by carrier name or by person id (two people may share a name), labelled by name.
  const buckets = new Map<string, { label: string; rows: RenewalRow[] }>();
  for (const r of rest) {
    const key =
      mode === 'carrier' ? `carrier:${r.carrier ?? ''}` : `person:${r.handledBy?.id ?? ''}`;
    const label = mode === 'carrier' ? carrierOf(r) : (r.handledBy?.name ?? 'Unassigned');
    const bucket = buckets.get(key) ?? { label, rows: [] };
    bucket.rows.push(r);
    buckets.set(key, bucket);
  }
  const soonest = (b: { rows: RenewalRow[] }) => Math.min(...b.rows.map((r) => r.daysLeft));
  for (const [key, b] of [...buckets].sort(
    (x, y) => soonest(x[1]) - soonest(y[1]) || x[1].label.localeCompare(y[1].label),
  )) {
    groups.push({ key, label: b.label, attention: false, rows: b.rows, meta: groupMeta(b.rows) });
  }
  return groups;
}

export type TeamChip = { id: string; name: string; open: number; attention: number };

/** The Manager's team strip: one chip per person with their open count and needs-attention count. */
export function teamOf(rows: RenewalRow[]): TeamChip[] {
  const team = new Map<string, TeamChip>();
  for (const r of rows) {
    if (!r.handledBy || stageOf(r) === 'closed') continue;
    const chip = team.get(r.handledBy.id) ?? {
      id: r.handledBy.id,
      name: r.handledBy.name,
      open: 0,
      attention: 0,
    };
    chip.open += 1;
    if (needsAttention(r)) chip.attention += 1;
    team.set(r.handledBy.id, chip);
  }
  return [...team.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export const teamTip = (c: TeamChip) =>
  c.attention > 0
    ? `${plural(c.open, 'open renewal', 'open renewals')}, ${c.attention} ${c.attention === 1 ? 'needs' : 'need'} attention.`
    : `${plural(c.open, 'open renewal', 'open renewals')}.`;

export type ViewKey = 'default' | 'expiry' | 'custom';

/** Which saved view the list matches: Default (grouped by carrier), By expiry (no groups), or Custom. */
export function viewOf(s: ListState): ViewKey {
  if (hasNarrowing(s)) return 'custom';
  if (s.group === 'carrier' && s.sort === 'days' && s.dir === 'asc') return 'default';
  if (s.group === 'none' && s.sort === 'days' && s.dir === 'asc') return 'expiry';
  return 'custom';
}

export const viewLabels: Record<ViewKey, string> = {
  default: 'Default',
  expiry: 'By expiry',
  custom: 'Custom',
};

/** A view's state: filters cleared, soonest first, grouped by carrier or not at all. */
export const viewState = (s: ListState, view: 'default' | 'expiry'): ListState => ({
  ...defaultState,
  q: s.q,
  group: view === 'default' ? 'carrier' : 'none',
});

/** The search's empty line (spec C7.3). */
export const noSearchMatch = (q: string) => `No renewals match "${q.trim()}".`;

/** The step sub-filter under Open (spec C4, SubTabs card), in step order. */
export const stepLabels: Record<CurrentStep, string> = {
  dec: 'Dec page',
  ams360: 'Check AMS360 / Renew in AMS360',
  email: 'Email insured',
  activity: 'Log activity',
  bank: 'Proof to bank',
  done: 'Done',
};
const stepOrder = Object.keys(stepLabels) as CurrentStep[];

export const isStepKey = (v: string | null): v is CurrentStep =>
  v !== null && (stepOrder as string[]).includes(v);

/** The steps with renewals on them, with their counts; steps with none are left out. */
export function stepCounts(rows: RenewalRow[]): { step: CurrentStep; label: string; n: number }[] {
  return stepOrder
    .map((step) => ({
      step,
      label: stepLabels[step],
      n: rows.filter((r) => r.currentStep === step).length,
    }))
    .filter((s) => s.n > 0);
}
